import { ref } from 'vue';
import { progressApi, type CourseProgressStatus, type CourseStatusMap } from '../services/progressApi';
import { courseDetailPath } from '../utils/credentialTypes';

/**
 * The kind whose rows get their own action wording (BPE-739).
 *
 * A simulation is not a course you read or watch — it is an interactive
 * exercise with voice conversations — and it earns no certificate, so the
 * generic ladder below has no completed rung for it and labelled a finished
 * simulation exactly like an untouched one.
 */
const SIMULATION_KIND = 'Simulation';

/**
 * A simulation row's action, borrowed from the simulation's own pre-launch
 * panel so the row and the screen behind it agree.
 *
 * In-progress is checked before completed because that is the state with
 * something to return to; a restart clears the completion, so an attempt in
 * flight always reads Resume.
 */
function simulationCtaLabel(status: CourseProgressStatus | undefined): string {
  if (status?.isInProgress) return 'Resume Simulation';
  if (status?.isCompleted) return 'Start Over';
  return 'Launch Simulation';
}

/**
 * BPE-571: `status.canClaimCertificate` is now the server's policy verdict, not
 * "video finished and nothing claimed". That matters most here — this is the CTA on the
 * History and Dashboard cards, and it is what routed a learner into `/course/:id/claim`
 * for a viewing that could never legitimately produce a certificate.
 *
 * Keep both functions reading the same field so the label and the destination can't disagree:
 * an ineligible enrollment must never be labelled "Claim Certificate" nor routed to the claim page.
 */
export function getCtaLabel(learningObjectId: number, statusMap: CourseStatusMap, variantKind?: string): string {
  const status = statusMap[learningObjectId];

  // The wording follows the row, not the screen: whichever variant the row
  // represents and links to decides it. A row shown as CLE keeps CLE wording
  // even when the course also has a simulation behind it, and vice versa —
  // which is why this branches on the kind rather than on the course's
  // variants, and why it sits above the certificate rungs. The status map is
  // per course, not per variant, so a dual CLE+Simulation course whose CLE
  // certificate is claimed would otherwise offer "View Certificate" on a row
  // the learner is reading as a simulation.
  if (variantKind === SIMULATION_KIND) return simulationCtaLabel(status);

  if (!status) return 'View Course';
  if (status.hasCertificate) return 'View Certificate';
  if (status.canClaimCertificate) return 'Claim Certificate';
  if (status.isInProgress) return 'Continue Course';
  return 'View Course';
}

export function getCtaUrl(learningObjectId: number, statusMap: CourseStatusMap, variantKind?: string): string {
  const status = statusMap[learningObjectId];
  const coursePath = courseDetailPath(learningObjectId, variantKind);

  // A simulation row always navigates to the course page, in all three states.
  // "Start Over" names where the learner is going, not something the row does:
  // the reset lives behind the simulation's own control, and wiring it to a
  // list row would let one click destroy a completed learner's record from a
  // page they were only browsing. Kept in step with the label above, so the
  // certificate destinations stay unreachable from a row that cannot offer
  // certificate wording.
  if (variantKind === SIMULATION_KIND) return coursePath;

  if (!status) return coursePath;
  if (status.hasCertificate) return `/certificates?course_id=${learningObjectId}`;
  if (status.canClaimCertificate) return `/course/${learningObjectId}/claim`;
  // Not claimable: send them to the course page, where the block reason is explained. Never to
  // the claim page — it would only bounce them off the server-side gate.
  // variantKind lenses the course link when the calling surface knows it;
  // kindless surfaces omit it and get the bare (union) URL.
  return coursePath;
}

export function useCourseStatus() {
  const courseStatusMap = ref<CourseStatusMap>({});

  function loadCourseStatus(learningObjectIds: number[]) {
    if (learningObjectIds.length === 0) return;
    progressApi.getCourseStatus(learningObjectIds).then((map) => {
      courseStatusMap.value = map;
    }).catch(() => { /* CTA falls back to "View Course" */ });
  }

  return { courseStatusMap, loadCourseStatus };
}
